import { ComparisonMode } from "@/gql/graphql";
import { TimelineFilterOption } from "@/gql/schema";
import { useState, useTransition } from "react";
import {
  filterOptions,
  getDatesFromFilterOption,
} from "@/app/Dashboard/Utils/filterOptions";
import { cn } from "@/lib/utils";
import { Select } from "@/components/catalyst/select";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { RegionalBookingStatBlock } from "@/app/Dashboard/Components/Blocks/Admin/RegionalBookingStatBlock";
import { DailyBookingStatBlock } from "@/app/Dashboard/Components/Blocks/DailyBookingStatBlock";
import { RelocationStatBlock } from "@/app/Dashboard/Components/Blocks/RelocationStatBlock";
import { WaitlistStatBlock } from "@/app/Dashboard/Components/Blocks/Admin/WaitlistStatBlock";
import { ProDriverStatBlock } from "@/app/Dashboard/Components/Blocks/ProDriverStatBlock";
import { WorkOrderStatBlock } from "@/app/Dashboard/Components/Blocks/Admin/WorkOrderStatBlock";
import { BookingStatusPieChartBlock } from "@/app/Dashboard/Components/Blocks/Supplier/BookingStatusPieChartBlock";
import { BookingListStatBlock } from "@/app/Dashboard/Components/Blocks/BookingListStatBlock";
import { RelocationMapBlock } from "@/app/Dashboard/Components/Blocks/RelocationMapBlock";
import { PendingInvoicesStatBlock } from "@/app/Dashboard/Components/Blocks/PendingInvoicesStatBlock";
import { AllSupplierBookingStatusBarChartBlock } from "@/app/Dashboard/Components/Blocks/AllSuppliers/AllSupplierBookingStatusBarChartBlock";
import { DashboardBlockBoundary } from "@/app/Dashboard/Components/Blocks/DashboardBlockBoundary";
import { Button } from "@/components/catalyst/button";
import { ArrowPathIcon } from "@heroicons/react/16/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

// GraphQL operation names backing the dashboard blocks. The refresh button
// scopes invalidation to these instead of every query in the app.
const DASHBOARD_QUERY_NAMES = new Set<string>([
  "DashboardStats",
  "AllSupplierDashboardBookingStats",
  "DashboardDepartingBookings",
  "PendingInvoices",
  "RelocationsByCity",
  "SupplierBookingStatBlock",
  "WorkOrderRegionalCounts",
  "WorkOrderStatusCount",
]);

const comparisonModeLabels = (
  t: ReturnType<typeof useTranslations>,
): Record<ComparisonMode, string> => ({
  [ComparisonMode.PreviousPeriod]: t("dashboard.comparisonMode.previousPeriod"),
  [ComparisonMode.PreviousYear]: t("dashboard.comparisonMode.previousYear"),
});

export function DashboardStatBlocks() {
  const { isAdmin } = useTenant();
  const { hasSupplier } = useGlobalSupplier();
  const t = useTranslations();
  const [filter, setFilter] = useState(TimelineFilterOption.SevenDays);
  const queryClient = useQueryClient();
  const [comparisonMode, setComparisonMode] = useState(
    ComparisonMode.PreviousYear,
  );
  const [isPending, startTransition] = useTransition();

  const { start, end } = getDatesFromFilterOption(filter);

  const isAdminView = isAdmin && !hasSupplier;

  return (
    <div
      className={cn("space-y-6", {
        "animate-pulse": isPending,
      })}
    >
      <div className="flex flex-col justify-between md:flex-row md:items-center">
        <div>
          <h2 className="text-2xl font-bold">{t("dashboard.title")}</h2>
          <p className="text-sm text-gray-500">
            {isAdminView
              ? t("dashboard.adminDescription")
              : t("dashboard.supplierDescription")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            outline
            isLoading={isPending}
            onClick={() => {
              startTransition(async () => {
                return queryClient.invalidateQueries({
                  predicate: (query) =>
                    DASHBOARD_QUERY_NAMES.has(String(query.queryKey[0])),
                });
              });
            }}
            LeadingIcon={ArrowPathIcon}
          />
          <Select
            name="comparisonMode"
            className="w-44"
            value={comparisonMode}
            onChange={(e) => {
              startTransition(() => {
                setComparisonMode(e.target.value as ComparisonMode);
              });
            }}
          >
            {Object.entries(comparisonModeLabels(t)).map(([mode, label]) => (
              <option key={mode} value={mode}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            name="filter"
            className="w-40"
            value={filter}
            onChange={(e) => {
              startTransition(() => {
                setFilter(e.target.value as TimelineFilterOption);
              });
            }}
          >
            {Object.entries(filterOptions).map(([option, label]) => (
              <option key={option} value={option}>
                {label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div className="grid gap-2 rounded-2xl bg-gray-100/80 sm:p-2 lg:grid-cols-2 [&>*]:min-w-0">
        {isAdminView ? (
          <>
            <DashboardBlockBoundary>
              <RegionalBookingStatBlock
                start={start}
                end={end}
                comparisonMode={comparisonMode}
              />
            </DashboardBlockBoundary>
            <DashboardBlockBoundary>
              <DailyBookingStatBlock
                start={start}
                end={end}
                comparisonMode={comparisonMode}
              />
            </DashboardBlockBoundary>
            <DashboardBlockBoundary>
              <RelocationStatBlock
                start={start}
                end={end}
                comparisonMode={comparisonMode}
              />
            </DashboardBlockBoundary>
            <DashboardBlockBoundary>
              <WaitlistStatBlock
                start={start}
                end={end}
                comparisonMode={comparisonMode}
              />
            </DashboardBlockBoundary>
            <DashboardBlockBoundary>
              <ProDriverStatBlock
                start={start}
                end={end}
                comparisonMode={comparisonMode}
              />
            </DashboardBlockBoundary>
            <DashboardBlockBoundary>
              <WorkOrderStatBlock
                start={start}
                end={end}
                comparisonMode={comparisonMode}
              />
            </DashboardBlockBoundary>
          </>
        ) : hasSupplier ? (
          <>
            <DashboardBlockBoundary>
              <RelocationMapBlock />
            </DashboardBlockBoundary>
            <DashboardBlockBoundary>
              <BookingStatusPieChartBlock
                start={start}
                end={end}
                comparisonMode={comparisonMode}
              />
            </DashboardBlockBoundary>
            <DashboardBlockBoundary>
              <BookingListStatBlock />
            </DashboardBlockBoundary>
            <DashboardBlockBoundary>
              <PendingInvoicesStatBlock />
            </DashboardBlockBoundary>
          </>
        ) : (
          <>
            <DashboardBlockBoundary>
              <RelocationMapBlock />
            </DashboardBlockBoundary>
            <DashboardBlockBoundary>
              <AllSupplierBookingStatusBarChartBlock
                start={start}
                end={end}
                comparisonMode={comparisonMode}
              />
            </DashboardBlockBoundary>
            <DashboardBlockBoundary>
              <BookingListStatBlock />
            </DashboardBlockBoundary>
            <DashboardBlockBoundary>
              <PendingInvoicesStatBlock />
            </DashboardBlockBoundary>
          </>
        )}
      </div>
    </div>
  );
}
